import React, { Suspense, useState } from 'react';
import {
    Crosshair, ExternalLink, Eye, Layers, Loader2, MapPin, MapPinOff, Move, Search, Check, ClipboardPaste,
} from 'lucide-react';
import { useToast } from '../../src/contexts/ToastContext';
import { corrigirLocalizacaoParada, localizarSugestao, type ParadaSalva } from '../../src/lib/rotaCobranca';
import { lerCoordenadaColada } from '../../src/lib/conferenciaRota';
import type { CamadaMapa } from '../CRM/Inadimplencia/components/MapaParadaConferencia';
import CampoEndereco, { type SugestaoEndereco } from './CampoEndereco';

// O mesmo mapa da conferência. Sob demanda: o Leaflet só baixa quando alguém
// abre uma parada — a tela da rota abre no 4G do carro.
const MapaParadaConferencia = React.lazy(() => import('../CRM/Inadimplencia/components/MapaParadaConferencia'));

/** O que o card sabe do cadastro da UC — só o que a localização usa. */
interface CadastroLocal {
    latitude?: number | null;
    longitude?: number | null;
    geocode_status?: string | null;
}

/** Onde o pino está e o quanto dá para confiar nele, dito para quem vai dirigir. */
const descreverPino = (parada: ParadaSalva, cadastro: CadastroLocal | null | undefined) => {
    if (parada.endereco_situacao === 'corrigido' && parada.endereco_lat != null && parada.endereco_lng != null) {
        return { lat: Number(parada.endereco_lat), lng: Number(parada.endereco_lng), exato: true, texto: 'Pino marcado à mão' };
    }
    if (cadastro?.latitude == null || cadastro?.longitude == null) return null;
    const lat = Number(cadastro.latitude);
    const lng = Number(cadastro.longitude);
    switch (cadastro.geocode_status) {
        case 'manual': return { lat, lng, exato: true, texto: 'Pino marcado à mão' };
        case 'endereco': return { lat, lng, exato: true, texto: 'Pino no endereço' };
        case 'rua': return { lat, lng, exato: true, texto: 'Pino na rua — sem número de porta, confira a casa' };
        case 'bairro': return { lat, lng, exato: false, texto: 'Pino aproximado: centro do bairro, não é a porta' };
        case 'cidade': return { lat, lng, exato: false, texto: 'Pino aproximado: centro da cidade, não é a porta' };
        default: return { lat, lng, exato: false, texto: 'Pino não conferido' };
    }
};

interface Props {
    parada: ParadaSalva;
    /** Cadastro da UC, lido quando o card abre. Nulo enquanto carrega ou sem UC. */
    cadastro: CadastroLocal | null | undefined;
    /** O endereço como está no cadastro — é o que vai na busca do Google Maps. */
    enderecoTexto: string;
    /** Parada já visitada: só mostra, não corrige. */
    somenteLeitura?: boolean;
    corrigindo: boolean;
    onCorrigindo: (v: boolean) => void;
    /** Gravou: a tela relê a rota. */
    onSalvo: () => void;
}

/**
 * A localização da parada no card: o mapa com o pino, o quanto ele é confiável
 * e como consertar.
 *
 * Antes o card só dizia "sem localização" e oferecia um campo de endereço — sem
 * mapa, sem ver onde o pino estava, sem abrir o Google Maps (pedido de
 * 24/09/2026). Agora é o mesmo mapa da conferência: pino arrastável, satélite,
 * Street View, e o link colado do Google Maps. Nada grava até "Salvar".
 */
const LocalizacaoParada: React.FC<Props> = ({
    parada, cadastro, enderecoTexto, somenteLeitura, corrigindo, onCorrigindo, onSalvo,
}) => {
    const { addToast } = useToast();
    const [camada, setCamada] = useState<CamadaMapa>('mapa');
    /** O pino novo, ainda não gravado. `rotulo` é o endereço escolhido na busca. */
    const [rascunho, setRascunho] = useState<{ lat: number; lng: number; rotulo: string | null } | null>(null);
    const [colado, setColado] = useState('');
    const [buscando, setBuscando] = useState(false);
    const [salvando, setSalvando] = useState(false);

    const atual = descreverPino(parada, cadastro);
    const pino = rascunho ?? atual;

    const fechar = () => { setRascunho(null); setColado(''); onCorrigindo(false); };

    const escolherSugestao = async (s: SugestaoEndereco) => {
        setBuscando(true);
        try {
            const ponto = await localizarSugestao(s.placeId);
            if (!ponto) { addToast('Não consegui achar esse endereço. Tente outra sugestão.', 'error'); return; }
            setRascunho({ lat: ponto.lat, lng: ponto.lng, rotulo: ponto.label || s.text });
        } catch (e: any) {
            addToast(e?.message || 'Falha ao buscar o endereço.', 'error');
        } finally {
            setBuscando(false);
        }
    };

    const usarColado = () => {
        const c = lerCoordenadaColada(colado);
        if (!c) {
            addToast('Não reconheci. Cole o link do Google Maps ou algo como "-16.6799, -49.2550".', 'error');
            return;
        }
        setRascunho({ lat: c.lat, lng: c.lng, rotulo: null });
        setColado('');
    };

    const salvar = async () => {
        if (!rascunho) return;
        setSalvando(true);
        try {
            await corrigirLocalizacaoParada(parada.id, parada.unidade_consumidora_id, {
                lat: rascunho.lat, lng: rascunho.lng, label: rascunho.rotulo || 'Marcado no mapa',
            });
            addToast('Localização salva — vale também para as próximas rotas.', 'success');
            setRascunho(null);
            onCorrigindo(false);
            onSalvo();
        } catch (e: any) {
            addToast(e?.message || 'Falha ao salvar a localização.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    const botaoLink = 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors';

    return (
        <div className={`rounded-xl border overflow-hidden ${pino ? 'border-gray-200 dark:border-gray-700' : 'border-red-200 dark:border-red-900/60'}`}>
            {/* O que o pino é, antes do mapa: pino aproximado com cara de certo é o erro que custa a viagem. */}
            <div className={`px-3 py-2 flex items-start gap-1.5 text-[11px] font-bold leading-snug ${
                rascunho ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'
                    : !atual ? 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
                        : atual.exato ? 'bg-gray-50 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300'
                            : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'}`}>
                {rascunho ? <Move size={13} className="shrink-0 mt-px" />
                    : atual ? <MapPin size={13} className="shrink-0 mt-px" />
                        : <MapPinOff size={13} className="shrink-0 mt-px" />}
                <span>
                    {rascunho
                        ? `Pino novo${rascunho.rotulo ? `: ${rascunho.rotulo}` : ''} — ainda não salvo`
                        : atual ? atual.texto : 'Sem pino no mapa'}
                </span>
            </div>

            {pino ? (
                <div className="h-60 relative overflow-hidden border-y border-gray-200 dark:border-gray-700">
                    <Suspense fallback={
                        <div className="h-full flex items-center justify-center text-xs text-gray-400">
                            <Loader2 size={14} className="animate-spin mr-2" /> abrindo o mapa…
                        </div>}>
                        <MapaParadaConferencia
                            lat={pino.lat}
                            lng={pino.lng}
                            arrastavel={corrigindo}
                            camada={camada}
                            onMover={(lat, lng) => setRascunho({ lat, lng, rotulo: null })}
                        />
                    </Suspense>
                    {corrigindo && (
                        <div className="absolute bottom-2 left-2 z-[400] px-2.5 py-1 rounded-full bg-gray-900/85 text-white text-[10px] font-bold flex items-center gap-1.5 pointer-events-none">
                            <Move size={11} /> arraste o pino até a porta
                        </div>
                    )}
                    {/* No mapa de ruas o pino no meio da quadra parece certo; na foto dá para ver o lote. */}
                    <div className="absolute top-2 right-2 z-[400] flex rounded-lg overflow-hidden shadow-md border border-white/70">
                        {(['mapa', 'satelite'] as const).map(c => (
                            <button
                                key={c}
                                onClick={() => setCamada(c)}
                                className={`px-2.5 py-1 text-[10px] font-bold flex items-center gap-1 transition-colors ${
                                    camada === c ? 'bg-gray-900 text-white' : 'bg-white/90 text-gray-600 hover:bg-white'}`}
                            >
                                {c === 'satelite' && <Layers size={11} />}
                                {c === 'mapa' ? 'Mapa' : 'Satélite'}
                            </button>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="px-3 py-5 bg-red-50/60 dark:bg-red-950/20 border-y border-red-200 dark:border-red-900/50 text-center">
                    <MapPinOff size={18} className="mx-auto text-red-500 mb-1.5" />
                    <p className="text-[11px] text-red-800 dark:text-red-300 leading-snug">
                        {corrigindo
                            ? 'Procure o endereço ou cole o link do Google Maps — o pino aparece aqui.'
                            : 'A busca automática não achou este endereço. Toque em "Marcar no mapa".'}
                    </p>
                </div>
            )}

            <div className="px-3 py-2.5 space-y-2.5">
                <div className="flex flex-wrap gap-1.5">
                    {/* Pelo TEXTO: é como achar a casa quando o pino não existe ou está longe. */}
                    {enderecoTexto && enderecoTexto !== '—' && (
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(enderecoTexto)}`}
                            target="_blank" rel="noopener noreferrer" className={botaoLink}>
                            <Search size={12} /> Procurar no Maps
                        </a>
                    )}
                    {pino && (
                        <>
                            <a href={`https://www.google.com/maps/search/?api=1&query=${pino.lat},${pino.lng}`}
                                target="_blank" rel="noopener noreferrer" className={botaoLink}>
                                <ExternalLink size={12} /> Ver o pino no Maps
                            </a>
                            {/* A fachada é o que o cobrador vai ver da rua. */}
                            <a href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${pino.lat},${pino.lng}`}
                                target="_blank" rel="noopener noreferrer" className={botaoLink}>
                                <Eye size={12} /> Street View
                            </a>
                        </>
                    )}
                    {!somenteLeitura && !corrigindo && (
                        <button onClick={() => onCorrigindo(true)}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${atual
                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                                : 'bg-red-600 hover:bg-red-700 text-white'}`}>
                            <Crosshair size={12} /> {atual ? 'Corrigir localização' : 'Marcar no mapa'}
                        </button>
                    )}
                </div>

                {corrigindo && (
                    <div className="rounded-lg bg-amber-50/70 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/40 p-2.5 space-y-2">
                        <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-snug">
                            Procure o endereço, arraste o pino até a porta ou cole o link do Google Maps
                            {' '}(no Maps, segure o dedo em cima da casa e copie).
                        </p>
                        <CampoEndereco
                            icone={buscando ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
                            placeholder="rua, número, bairro, cidade"
                            rotulo="Procurar o endereço da parada"
                            disabled={buscando || salvando}
                            onEscolher={s => void escolherSugestao(s)}
                        />
                        <div className="flex gap-1.5">
                            <input
                                value={colado}
                                onChange={e => setColado(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') usarColado(); }}
                                placeholder="ou cole o link / a coordenada do Maps"
                                aria-label="Link ou coordenada do Google Maps"
                                className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-[#5F6C37]"
                            />
                            <button onClick={usarColado} disabled={!colado.trim()}
                                className="flex items-center gap-1 px-3 py-2 rounded-lg bg-gray-800 dark:bg-gray-700 text-white text-[11px] font-bold disabled:opacity-40">
                                <ClipboardPaste size={12} /> Usar
                            </button>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={fechar} disabled={salvando}
                                className="px-3 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 text-xs font-bold disabled:opacity-50">
                                Cancelar
                            </button>
                            <button onClick={() => void salvar()} disabled={!rascunho || salvando}
                                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[#5F6C37] hover:bg-[#4a5429] text-white text-xs font-bold transition-colors disabled:opacity-40">
                                {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                {rascunho ? 'Salvar localização' : 'Mova o pino para salvar'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LocalizacaoParada;
